import { zodToEffectSchema } from "@redotech/effect-api-shared-common";
import { Schema } from "effect";
import * as z from "zod";
import { SupportTicketId } from "../../../ids/definitions";
import {
  FIXED_SUPPORT_TICKET_SNOOZE_OPTIONS,
  SUPPORT_TICKET_STATUSES,
  type FixedSupportTicketSnoozeOption,
} from "./support-ticket";

export const BulkSupportTicketSnoozeDurationZod = z.enum(
  FIXED_SUPPORT_TICKET_SNOOZE_OPTIONS,
);
export type BulkSupportTicketSnoozeDuration = FixedSupportTicketSnoozeOption;

export const BulkSupportTicketActionZod = z.discriminatedUnion("_tag", [
  z.object({
    _tag: z.literal("setSupportTicketStatus"),
    status: z.enum(SUPPORT_TICKET_STATUSES),
  }),
  z.object({
    _tag: z.literal("snoozeSupportTickets"),
    duration: BulkSupportTicketSnoozeDurationZod,
  }),
  z.object({
    _tag: z.literal("setReadStatus"),
    status: z.enum(["read", "unread"]),
  }),
  z.object({ _tag: z.literal("setAssignee"), userId: z.string() }),
  z.object({ _tag: z.literal("unassignSupportTickets") }),
  z.object({ _tag: z.literal("addTags"), tagIds: z.array(z.string()) }),
  z.object({ _tag: z.literal("removeTags"), tagIds: z.array(z.string()) }),
  z.object({
    _tag: z.literal("mergeSupportTickets"),
    destinationSupportTicketId: z.string(),
    mongoCustomerId: z.string().nullable(),
    mongoAssigneeId: z.string().nullable(),
    status: z.enum(SUPPORT_TICKET_STATUSES),
    priority: z.enum(["URGENT"]).nullish(),
  }),
  z.object({ _tag: z.literal("markSpam") }),
  z.object({ _tag: z.literal("unmarkSpam") }),
]);

export const BulkSupportTicketAction = zodToEffectSchema(
  BulkSupportTicketActionZod,
);
export type BulkSupportTicketAction = typeof BulkSupportTicketAction.Type;

export const StartBulkSupportTicketActionInput = Schema.Struct({
  supportTicketIds: Schema.Array(SupportTicketId),
  action: BulkSupportTicketAction,
});
export type StartBulkSupportTicketActionInput =
  typeof StartBulkSupportTicketActionInput.Type;

/**
 * One per account the bulk-action workflow was started for. Connected-team
 * callers can submit ticket ids belonging to multiple accounts in a single
 * request and we fan out one workflow per account.
 */
export const BulkSupportTicketActionWorkflowResult = Schema.Struct({
  workflowId: Schema.String,
  accountId: Schema.String,
  supportTicketCount: Schema.Number,
});
export type BulkSupportTicketActionWorkflowResult =
  typeof BulkSupportTicketActionWorkflowResult.Type;

export const StartBulkSupportTicketActionResponse = Schema.Struct({
  workflows: Schema.Array(BulkSupportTicketActionWorkflowResult),
});
export type StartBulkSupportTicketActionResponse =
  typeof StartBulkSupportTicketActionResponse.Type;

export const StartBulkSupportTicketActionByFilterInput = Schema.Struct({
  filterGroup: Schema.optional(Schema.String),
  excludedSupportTicketIds: Schema.optional(Schema.Array(SupportTicketId)),
  action: BulkSupportTicketAction,
});
export type StartBulkSupportTicketActionByFilterInput =
  typeof StartBulkSupportTicketActionByFilterInput.Type;

export const StartBulkSupportTicketActionByFilterResponse = Schema.Struct({
  workflows: Schema.Array(BulkSupportTicketActionWorkflowResult),
  supportTicketCount: Schema.Number,
});
export type StartBulkSupportTicketActionByFilterResponse =
  typeof StartBulkSupportTicketActionByFilterResponse.Type;
